// Created by jarongallo at 3/31/21
import React, { useEffect, useState } from 'react';
import './styles';
import UILabeledInput from '../../../../../components/UI/UILabeledInput';
import UIRegionRow from '../../../../../components/UI/UIRegionRow/UIRegionRow';
import UIModal from '../../../../../components/UI/UIModal';
import TechnicianCoverageArea from '../../../../../components/modals/TechnicianCoverageAreaModal';
import { UI_LABELED_INPUT_DROPDOWN_TYPE } from '../../../../../components/UI/UILabeledInput/config';
import Queries from '../../../../../utils/API/Queries';
import { toast } from 'react-toastify';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import { DropdownItem } from '../../../../../components/UI/UILabeledInput/types';
import { GET_TECHNICIAN_SERVICE_AREA, GET_TECHNICIAN_SERVICE_AREA_REGIONS } from './queries';
import { useSelector } from 'react-redux';
import { getSignedInUserSelector } from '../../../../../redux/selectors/CurrentUserSelectors';
import UIListInputRow from '../../../../../components/UI/UIListInputRow';
import { CHILD_TYPE } from '../../../../../components/UI/UIListInputRow/config';
import UIIconButton from '../../../../../components/UI/UIIconButton';
import * as strings from './strings';
import { camelCaseToSentence } from '../../../../../utils/Helpers/StringHelpers';
import { UI_ICON_BUTTON_FILL_TYPE } from '../../../../../components/UI/UIIconButton/config';

export default function IndividualTechnicianServiceArea({ technicianId }: IndividualTechnicianServiceAreaProps) {
	const signedInUser = useSelector(getSignedInUserSelector);

	const [showZipModal, setShowZipModal] = useState(false);
	const [technician, setTechnician] = useState<UserType>();
	const [selectedDropdown, setSelectedDropdown] = useState<DropdownItem | null>();
	const [regions, setRegions] = useState<RegionType[]>();
	const [adminsRegions] = useState<RegionType[]>(signedInUser.regions);

	const [getTechnician, { data: technicianData }] = useLazyQuery(GET_TECHNICIAN_SERVICE_AREA, {
		variables: { id: technicianId }
	});

	const [getRegions, { data: regionsData }] = useLazyQuery(GET_TECHNICIAN_SERVICE_AREA_REGIONS, {
		variables: { query: { id: { op_in: technician?.regions.map((region) => region.id) ?? [] } } }
	});

	const [updateTechnician] = useMutation(Queries.UPDATE_USER_MUTATION);

	useEffect(() => {
		(async () => {
			await getTechnician();
		})();
	}, []);

	useEffect(() => {
		(async () => {
			if (technicianData) {
				const fetchedTechnician: UserType = technicianData['user'];
				await getRegions({ variables: { query: { id: { op_in: fetchedTechnician.regions.map((region) => region.id) ?? [] } } } });
				setTechnician(fetchedTechnician);
			}
		})();
	}, [technicianData]);

	useEffect(() => {
		(() => {
			if (regionsData) {
				const fetchedRegions: RegionType[] = regionsData['regions'];
				setRegions(fetchedRegions);
			}
		})();
	}, [regionsData]);

	if (!technician || !regions) return null;

	const onChangeOption = async (optionName: string, value: any) => {
		let variables = { id: technicianId };
		variables[optionName] = value;
		const response = await updateTechnician({ variables });
		const { updateUser } = response['data'];
		if (updateUser) {
			await getRegions();
			toast.success(strings.updateUserSuccess(camelCaseToSentence(optionName)));
		} else {
			toast.error(strings.errorUpdatingUser('Error updating user!'));
		}
	};

	const renderZipSelectModal = () => {
		const hideModal = () => setShowZipModal(false);

		return (
			<UIModal condition={showZipModal}>
				<TechnicianCoverageArea regions={regions} technician={technician} hideModal={hideModal} />
			</UIModal>
		);
	};

	const renderRegionComponent = () => {
		const getOptionsDropdown = () => {
			let optionsDropdown: DropdownItem[] = [];

			for (let adminRegion of adminsRegions) {
				if (regions.map((region) => region.id).indexOf(adminRegion.id) === -1) {
					optionsDropdown.push({ value: adminRegion, label: adminRegion.name });
				}
			}

			return optionsDropdown;
		};
		const onSelectRegion = async (dropdownItem) => {
			const newRegions = [...regions, dropdownItem.value].map((region) => region.id);
			await onChangeOption('regions', newRegions);
			setSelectedDropdown(null);
		};

		const optionsDropdown = getOptionsDropdown();

		return (
			<UIListInputRow
				type={CHILD_TYPE}
				label={optionsDropdown.length === 0 ? strings.regionsLabel : strings.addRegionLabel}
				className='individualTechnicianServiceArea__regions'>
				{optionsDropdown.length > 0 && (
					<UILabeledInput
						key={`dropdown-input:${selectedDropdown}`}
						type={UI_LABELED_INPUT_DROPDOWN_TYPE}
						containerStyle={{ width: 288, marginBottom: 16 }}
						style={{ width: '100%' }}
						options={optionsDropdown}
						value={selectedDropdown}
						onChangeDropdownValue={onSelectRegion}
						placeholder={strings.addRegionPlaceholder}
					/>
				)}
				{regions.map((region) => {
					const onRemoveRegion = async () => {
						let regionsMap = regions;
						for (let i = 0; i < regionsMap.length; i++) {
							if (regionsMap[i] === region) {
								regionsMap.splice(i, 1);
							}
						}
						const newRegions = regionsMap.map((region) => region.id);
						await onChangeOption('regions', newRegions);
					};
					return <UIRegionRow key={region.id} region={region} onRemove={onRemoveRegion} />;
				})}
			</UIListInputRow>
		);
	};

	return (
		<div className='individualTechnicianServiceArea'>
			<UIListInputRow type={CHILD_TYPE} label={strings.zipCodesLabel}>
				<UIIconButton
					dark
					type={UI_ICON_BUTTON_FILL_TYPE}
					className='individualTechnicianServiceArea--zipCodes'
					onClick={() => setShowZipModal(true)}
					text={strings.editZipCodes}
				/>
			</UIListInputRow>
			{renderRegionComponent()}
			{renderZipSelectModal()}
		</div>
	);
}
